import React, { useEffect, useState } from "react";
import axios from "../../utility/customAxios";
import { Card, CardContent } from "../ui/card";
import { Separator } from "../ui/separator";

interface UsageCostEstimateProps {
    className?: string;
}

interface DataPoint {
    time: number;
    value: number | null;
}

interface TimeSeries {
    data: DataPoint[];
    attributes: Record<string, string>;
}

interface Metric {
    name: string;
    timeSeries: TimeSeries[];
    type: string;
    attributes: Record<string, string[]>;
    unit: string;
}

interface UsageDataResponse {
    metric: Metric;
    isResultLimited: boolean;
    resultLen: number;
}

export function UsageCostEstimate({ className }: UsageCostEstimateProps) {
    const [nodeMinutesLastWeek, setNodeMinutesLastWeek] = useState<number>(0);
    const [dataUsageLastWeek, setDataUsageLastWeek] = useState<number>(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Constants for pricing
    const MONTHS_IN_WEEK = 4.33333;
    const DIVISOR_FOR_NODES = 43200; // Number of minutes in a month (30 days * 24 hours * 60 minutes)
    const PRICE_PER_NODE = 20; // $20 per node
    const INCLUDED_GB_PER_NODE = 100; // 100GB included per node
    const EXCESS_DATA_PRICE_PER_GB = 0.20; // $0.20 per GB

    useEffect(() => {
        fetchUsageData();
    }, []);

    const fetchUsageData = async () => {
        setLoading(true);
        setError(null);
        
        try {
            // Calculate time range for last week
            const endTime = Date.now();
            const startTime = endTime - (7 * 24 * 60 * 60 * 1000); // 7 days ago

            // Fetch node minutes for last week
            const nodeMinutesRequest = {
                startTime,
                endTime,
                bucketSize: 3600, // 1 hour
            };

            const nodeMinutesResponse = await axios.post<UsageDataResponse>('/api/v1/billing/usage', nodeMinutesRequest);
            
            // Sum up all node minutes values
            let totalNodeMinutes = 0;
            if (nodeMinutesResponse.data.metric.timeSeries) {
                nodeMinutesResponse.data.metric.timeSeries.forEach((series) => {
                    series.data.forEach((dataPoint) => {
                        if (dataPoint.value !== null) {
                            totalNodeMinutes += dataPoint.value;
                        }
                    });
                });
            }
            setNodeMinutesLastWeek(totalNodeMinutes);

            // Fetch data usage for last week
            const dataUsageRequest = {
                startTime,
                endTime,
                bucketSize: 3600, // 1 hour
            };

            const dataUsageResponse = await axios.post<UsageDataResponse>('/api/v1/billing/getDataSent', dataUsageRequest);
            
            // Sum up all data usage values (in bytes)
            let totalDataBytes = 0;
            if (dataUsageResponse.data.metric.timeSeries) {
                dataUsageResponse.data.metric.timeSeries.forEach((series) => {
                    series.data.forEach((dataPoint) => {
                        if (dataPoint.value !== null) {
                            totalDataBytes += dataPoint.value;
                        }
                    });
                });
            }
            // Convert bytes to GB
            setDataUsageLastWeek(totalDataBytes / (1024 * 1024 * 1024));

        } catch (err) {
            console.error("Failed to fetch usage data:", err);
            setError("Failed to fetch usage data");
        } finally {
            setLoading(false);
        }
    };

    // Calculation steps
    const monthlyNodeMinutes = nodeMinutesLastWeek * MONTHS_IN_WEEK;
    const averageNodes = monthlyNodeMinutes / DIVISOR_FOR_NODES;
    const nodeCost = averageNodes * PRICE_PER_NODE;
    
    const monthlyDataUsageGB = dataUsageLastWeek * MONTHS_IN_WEEK;
    const includedDataGB = averageNodes * INCLUDED_GB_PER_NODE;
    const excessDataGB = Math.max(0, monthlyDataUsageGB - includedDataGB);
    const excessDataCost = excessDataGB * EXCESS_DATA_PRICE_PER_GB;
    
    const totalCost = nodeCost + excessDataCost;

    const formatNumber = (num: number, decimals: number = 2) => {
        return num.toFixed(decimals);
    };

    const formatCurrency = (num: number) => {
        return `$${num.toFixed(2)}`;
    };

    if (loading) {
        return (
            <div className={`flex flex-col gap-6 ${className}`}>
                <div className="text-center">
                    <h2 className="text-2xl font-semibold text-textlight mb-2">Usage Cost Estimate</h2>
                    <p className="text-textmedium">Loading usage data...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={`flex flex-col gap-6 ${className}`}>
                <div className="text-center">
                    <h2 className="text-2xl font-semibold text-textlight mb-2">Usage Cost Estimate</h2>
                    <p className="text-red-500">{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className={`flex flex-col gap-6 ${className}`}>
            <div className="text-center">
                <h2 className="text-2xl font-semibold text-textlight mb-2">Usage Cost Estimate</h2>
                <p className="text-textmedium">Based on your usage from the last 7 days, extrapolated to a full month.</p>
            </div>
            <h3  className="text-xl font-semibold text-textlight">How it's calculated</h3>
            <p>
                Metoro charges based on two dimensions: <strong>node minutes</strong> and <strong>data usage</strong>.
                Monitoring a node for a full month incurs a cost of $20 per node. We record node usage at the minute level, so if you brought a node online for 10 minutes and then took it offline, you would be charged for 10 minutes of usage.
                Each node includes 100GiB of data usage per month. Any data usage beyond this allowance incurs a charge of <b>$0.20 / GiB</b> of uncompressed data ingested.
            </p>
            <p>
                Below is a detailed breakdown of how your estimated monthly cost is calculated based on your last week of usage. Note that this is an estimate and actual costs may vary based on your usage patterns.
            </p>
            <p>
                Any seperate agreements or discounts you may have with Metoro will not be reflected in this estimate. Please refer to your contract or contact your Metoro representative for specific pricing details / bulk discounts.
                If you do not have a direct Metoro representative, please reach out to <a href="mailto:sales@metoro.io" className="text-primary hover:underline">via email</a> or over intercom on metoro.io and we will assign one to you.
            </p>
            
            <div className="">
                <Card className={"border-none"}>
                    <CardContent className="space-y-2 pt-6">
                {/* Step 1: Last Week Usage */}
                <div>
                    <h3 className="text-sm font-semibold text-textmedium mb-6">Step 1: Last Week Usage</h3>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1">
                            <p className="text-sm text-textmedium">Node Minutes</p>
                            <p className="text-2xl font-semibold">{formatNumber(nodeMinutesLastWeek, 0)}</p>
                        </div>
                        <div className="space-y-1">
                            <p className="text-sm text-textmedium">Data Usage</p>
                            <p className="text-2xl font-semibold">{formatNumber(dataUsageLastWeek, 2)} GiB</p>
                        </div>
                    </div>
                    <div className="mt-4 p-3 bg-backgrounddarker rounded-md">
                        <p className="text-xs text-textmedium">
                            <span className="font-semibold">How it's calculated:</span> We sum all node minutes and data ingested over the last 7 days.
                        </p>
                    </div>
                </div>

                <Separator />

                {/* Step 2: Monthly Projections */}
                <div>
                    <h3 className="text-sm font-semibold text-textmedium mb-6">Step 2: Monthly Projections</h3>
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1">
                                <p className="text-sm text-textmedium">Monthly Node Minutes</p>
                                <p className="text-xl font-semibold">{formatNumber(monthlyNodeMinutes, 0)}</p>
                            </div>
                            <div className="space-y-1">
                                <p className="text-sm text-textmedium">Monthly Data Usage</p>
                                <p className="text-xl font-semibold">{formatNumber(monthlyDataUsageGB, 2)} GiB</p>
                            </div>
                        </div>
                    </div>
                    <div className="mt-4 p-3 bg-backgrounddarker rounded-md">
                        <p className="text-xs text-textmedium">
                            <span className="font-semibold">How it's calculated:</span> Weekly usage is multiplied by 4.33 (average weeks per month) to estimate monthly usage.
                        </p>
                    </div>
                </div>

                <Separator />

                {/* Step 3: Node Cost Calculation */}
                <div>
                    <h3 className="text-sm font-semibold text-textmedium mb-6">Step 3: Node Cost</h3>
                    <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-textmedium">Average Nodes = Monthly Minutes ÷ 43,200</span>
                            <span className="font-mono">{formatNumber(averageNodes, 2)} nodes</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-textmedium">Node Cost = {formatNumber(averageNodes, 2)} × $20/node</span>
                            <span className="font-mono font-semibold">{formatCurrency(nodeCost)}</span>
                        </div>
                    </div>
                    <div className="mt-4 p-3 bg-backgrounddarker rounded-md">
                        <p className="text-xs text-textmedium">
                            <span className="font-semibold">How it's calculated:</span> Monthly node minutes divided by 86,400 gives the average number of nodes. Each node costs $20/month.
                        </p>
                    </div>
                </div>

                <Separator />

                {/* Step 4: Data Cost Calculation */}
                <div>
                    <h3 className="text-sm font-semibold text-textmedium mb-6">Step 4: Data Cost</h3>
                    <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-textmedium">Included Data = {formatNumber(averageNodes, 2)} nodes × 100 GiB</span>
                            <span className="font-mono">{formatNumber(includedDataGB, 2)} GB</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-textmedium">Excess Data = {formatNumber(monthlyDataUsageGB, 2)} GiB - {formatNumber(includedDataGB, 2)} GiB</span>
                            <span className="font-mono">{formatNumber(excessDataGB, 2)} GB</span>
                        </div>
                        {excessDataGB > 0 && (
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-textmedium">Excess Data Cost = {formatNumber(excessDataGB, 2)} × $0.20/GiB</span>
                                <span className="font-mono font-semibold">{formatCurrency(excessDataCost)}</span>
                            </div>
                        )}
                        {excessDataGB <= 0 && (
                            <div className="text-sm text-green-600">
                                ✓ No excess data charges - usage within included allowance
                            </div>
                        )}
                    </div>
                    <div className="mt-4 p-3 bg-backgrounddarker rounded-md">
                        <p className="text-xs text-textmedium">
                            <span className="font-semibold">How it's calculated:</span> Each node includes 100GiB of data. Usage beyond this is charged at $0.20/GiB for the excess amount.
                        </p>
                    </div>
                </div>

                <Separator />

                {/* Total Cost */}
                <div className="bg-primarytransparenter rounded-lg">
                    <h3 className="text-sm font-semibold text-textmedium mb-6">Estimated Monthly Cost</h3>
                    <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-textmedium">Node Cost</span>
                            <span className="font-mono">{formatCurrency(nodeCost)}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-textmedium">Excess Data Cost</span>
                            <span className="font-mono">{formatCurrency(excessDataCost)}</span>
                        </div>
                        <Separator className="my-2" />
                        <div className="flex items-center justify-between">
                            <span className="font-semibold">Total</span>
                            <span className="text-2xl font-bold text-primary">{formatCurrency(totalCost)}</span>
                        </div>
                    </div>
                </div>

                <p className="text-xs text-textmedium text-center mt-4">
                    * This is an estimate based on your last 7 days of usage. Actual costs may vary.
                </p>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}